import { RefObject, useEffect } from 'react';
import { getCountryListPosition, showInTopLayer } from '../phoneField.utils';

const CONTAINER_SELECTOR = '.react-tel-input';
const COUNTRY_LIST_SELECTOR = '.country-list';
const HIGHLIGHTED_COUNTRY_SELECTOR = '.country.highlight';

export const useCountryListPosition = (inputRef: RefObject<HTMLInputElement | null>) => {
  useEffect(() => {
    const container = inputRef.current?.closest<HTMLElement>(CONTAINER_SELECTOR);

    if (!container) {
      return;
    }

    let trackedList: HTMLElement | null = null;
    let trackedListHeight = 0;

    const updatePosition = () => {
      const list = trackedList;

      if (!list) {
        return;
      }

      const { top, left, maxHeight } = getCountryListPosition({
        anchorRect: container.getBoundingClientRect(),
        listSize: { width: list.offsetWidth, height: trackedListHeight },
        viewportSize: {
          width: document.documentElement.clientWidth,
          height: document.documentElement.clientHeight,
        },
      });

      list.style.top = `${top}px`;
      list.style.left = `${left}px`;
      list.style.maxHeight = `${maxHeight}px`;
    };

    const stopTracking = () => {
      trackedList = null;
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };

    const observer = new MutationObserver(() => {
      const list = container.querySelector<HTMLElement>(COUNTRY_LIST_SELECTOR);

      if (!list) {
        stopTracking();
        return;
      }

      if (list === trackedList) {
        return;
      }

      trackedList = list;
      trackedListHeight = list.offsetHeight;
      showInTopLayer(list);
      updatePosition();
      list.querySelector(HIGHLIGHTED_COUNTRY_SELECTOR)?.scrollIntoView({ block: 'nearest' });
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    });

    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      stopTracking();
    };
  }, [inputRef]);
};
